import { Fragment, useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ACCOUNT_KIND_DEFS,
  ACCOUNT_KIND_MAP,
  ACTIVITY_TYPE_MAP,
  LEAD_RATING_DEFS,
  LEAD_RATING_MAP,
  LEAD_STATUS_DEFS,
  LEAD_STATUS_MAP,
  QUOTATION_STATUS_MAP,
  renderMentions,
  whatsappNumber,
  type AccountKind,
  type FeedbackView,
  type LeadStatus,
  type MessageChannel,
} from "@goyone/shared";
import { api, qs, upload } from "../../lib/api";
import { date, dateTime, money, plural, WORDS } from "../../lib/format";
import { Icon } from "../../components/Icon";
import { LeadEditor } from "./LeadEditor";
import { DeleteButton } from "../../components/DeleteDialog";
import { activeFields, useCustomFields } from "../../components/CustomFields";
import { Badge, ErrorState, Modal, PageHead } from "../../components/ui";
import { RecordStats, type RecordStat } from "../../components/RecordStats";
import { CardPickerButton, useCardChoice } from "../../components/CardPicker";
import {
  LayoutColumn,
  ProfileLayoutBar,
  ProfileLayoutButton,
  ProfileLayoutProvider,
  Section,
  useProfileLayout,
  type SectionDef,
} from "../../components/ProfileLayout";
import { Attachments } from "../../components/Attachments";
import { Avatar, OwnerChip, useTeamDirectory } from "../../components/People";
import { RecordTimeline } from "../../components/RecordTimeline";
import { Feedback } from "../../components/Feedback";
import { TimeLog, TimerButton } from "../../components/WorkTimer";
import { ActivityHub } from "./ActivityHub";
import { MailCompose } from "../../components/MailCompose";
import {
  QuickMessageModal,
  type QuickChannel,
} from "../../components/QuickMessage";
import { QuickCallModal } from "../../components/QuickCall";
import { useToast } from "../../components/Toast";
import { ChannelLogo } from "../../components/ChannelLogo";
import { RatingMeter } from "../../components/RatingMeter";
import { BookingLinkButton } from "../../components/BookingLink";
import { LeadQuickActions } from "./LeadQuickActions";
import { QuotationsBoard } from "./AccountProfile";

interface Lead {
  id: string;
  name: string;
  company: string | null;
  jobTitle: string | null;
  email: string | null;
  phone: string | null;
  altPhone: string | null;
  messengerHandle: string | null;
  instagramHandle: string | null;
  /** توجيه القنوات: أيُّ الرقمين يستقبل كل قناة. */
  waPhone: "phone" | "altPhone";
  smsPhone: "phone" | "altPhone";
  /** رابط موقع العميل على الخرائط — للاجتماعات الحضورية. */
  mapsUrl: string | null;
  source: string | null;
  industry: string | null;
  city: string | null;
  address: string | null;
  notes: string | null;
  /** الصورة `data:` — تحلّ محلّ حروف الاسم حين توجد. */
  avatarUrl: string | null;
  status: LeadStatus;
  rating: "HOT" | "WARM" | "COLD";
  value: string | null;
  ownerId: string | null;
  lastContactedAt: string | null;
  nextActionAt: string | null;
  convertedAt: string | null;
  convertedAccountId: string | null;
  convertedDealId: string | null;
  createdAt: string;
  /** قيم الحقول المخصَّصة، بمفاتيحها المولَّدة. */
  customFields: Record<string, unknown> | null;
  /*
   * الفهرس مفتوح ليقبله المحرِّر.
   *
   * المحرِّر يقرأ الحقول بأسمائها نصًّا ليخدم العميل والعميل المحتمل بنموذج
   * واحد، فيحتاج نوعًا يسمح بذلك.
   */
  [key: string]: unknown;
}

interface Activity {
  id: string;
  type: "TASK" | "MEETING" | "CALL" | "MAIL" | "NOTE";
  status: "PLANNED" | "DONE" | "CANCELLED";
  subject: string;
  body: string | null;
  dueAt: string | null;
  completedAt: string | null;
  createdAt?: string;
  /** مهمة المشروع المزروعة — النقر يفتح لوحها للعمل منها. */
  agencyTaskId?: string | null;
  meetingProvider?: string | null;
  meetingUrl?: string | null;
  callDirection?: string | null;
  callOutcome?: string | null;
  mailTo?: string | null;
  mailSubject?: string | null;
  messageChannel?: string | null;
  /** تصنيف الملاحظة: أيَّ جانبٍ تخصّ — TASK/MEETING/CALL/MAIL، والفارغ عامّة. */
  noteAbout?: string | null;
  /** رابط تسجيل الاجتماع — يُشغَّل من الصفّ ومن التفاصيل. */
  recordingUrl?: string | null;
}

/*
 * ميتر المراحل: التعبئة تدرّجٌ من الأزرق عند «جديد» إلى لون المرحلة
 * الحالية — فالمزاج يُقرأ من اللون: الأخضر تأهّل، والأحمر الخفيف
 * فقدان أهلية، والصريح استبعاد.
 */
const STAGE_METER_START = "#3672EA";

const STAGE_METER_END: Record<string, string> = {
  NEW: "#3672EA",
  CONTACTED: "#2E8BD9",
  WORKING: "#1F9E8E",
  QUALIFIED: "#2FA24C",
  UNQUALIFIED: "#E58B7B",
  JUNK: "#D63A3A",
};

/** مزج لونين ست عشريين بنسبة t — لحساب لون كل خلية على الميتر. */
function lerpHex(a: string, b: string, t: number): string {
  const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
  const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
  return `#${pa
    .map((x, k) =>
      Math.round(x + (pb[k]! - x) * Math.min(1, Math.max(0, t)))
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;
}

/**
 * «@اسم» من معرّفٍ أو رابطٍ كامل — والرابط الصالح للفتح.
 *
 * من يلصق رابب إنستغرام كاملًا يرى «@اسم» بأيقونته لا سطرًا يلتفّ،
 * ومن يكتب المعرّف وحده يُبنى له الرابط.
 */
function socialRef(
  raw: string,
  base: "instagram" | "messenger",
): { handle: string; href: string } {
  const t = raw.trim();
  const m = t.match(/^https?:\/\/[^/]+\/([^/?#]+)/i);
  const handle = (m ? m[1]! : t).replace(/^@/, "");
  const href = /^https?:\/\//i.test(t)
    ? t
    : base === "instagram"
      ? `https://instagram.com/${encodeURIComponent(handle)}`
      : `https://m.me/${encodeURIComponent(handle)}`;
  return { handle: `@${handle}`, href };
}

/* الافتراضي: ما كان معروضًا قبل أن يصير اختيار البطاقات متاحًا. */
const LEAD_CARD_DEFAULTS = ["owner", "lastContact", "next", "touches", "value"];

/* أقسام ملف العميل المحتمل الكبيرة — ما يُسحب ويُخفى ويُكبَّر. */
const LEAD_SECTIONS: SectionDef[] = [
  { key: "hub", title: "سجل التعامل", column: "main" },
  { key: "deals", title: "الصفقات", column: "main" },
  { key: "feedback", title: "ملاحظات الفريق", column: "main" },
  { key: "attachments", title: "المرفقات", column: "main" },
  { key: "data", title: "البيانات", column: "side" },
  { key: "timeline", title: "ما جرى مع هذا العميل", column: "side" },
  { key: "timelog", title: "سجل الوقت", column: "side" },
];

export function LeadDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [converting, setConverting] = useState(false);
  /* «حوّله الآن» من إشعارٍ أو من صفحة العرض يفتح نافذة التحويل فورًا. */
  const [searchParams, setSearchParams] = useSearchParams();
  /*
   * موافقةٌ تنتظر التحويل.
   *
   * جاء من عرضٍ قبِله المحتمل: يُحوَّل هنا، ثم تُسجَّل الموافقةُ على العرض
   * تلقائيًّا وقد صار على الحساب — فلا تُترك موافقةٌ بلا حسابٍ ولا حسابٌ
   * بلا موافقةٍ سُجّلت.
   */
  const [pendingAccept, setPendingAccept] = useState<string | null>(null);
  useEffect(() => {
    const accept = searchParams.get("accept");
    if (accept) setPendingAccept(accept);
    if (searchParams.get("convert") === "1") {
      setConverting(true);
      searchParams.delete("convert");
      searchParams.delete("accept");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /* اختيار البطاقات مرفوعٌ إلى الرأس — المنتقي بجوار النقاط لا تحتها. */
  const cardChoice = useCardChoice("goyone.stats.lead", LEAD_CARD_DEFAULTS);
  /* نمط الملف: ترتيب أقسامه وإخفاؤها وتكبير واحدها — تفضيلٌ يبقى. */
  const layout = useProfileLayout("goyone.layout.lead", LEAD_SECTIONS);
  const [editing, setEditing] = useState(false);
  /* المُرسِل يعمل من موضعين: صفّ نشاطٍ يُكمِله الإرسال، أو زرّ الرأس بلا نشاط. */
  const [quickMsg, setQuickMsg] = useState<{
    channel: QuickChannel;
    activity: Activity | null;
  } | null>(null);
  const [headerMail, setHeaderMail] = useState(false);
  /* لوحة الاتصال الراسية — الكونسول يحضر إلى الملف بدل الإبحار إليه. */
  /*
   * الرقم المطلوب — لا رايةَ فتحٍ فحسب.
   *
   * الاتصال يُطلب من الرأس ومن لوحة البيانات (الأساسيّ والآخر)، فلو كانت
   * الحالة رايةً لطلبت اللوحةُ الرقمَ الأساسيّ دائمًا — ومن ضغط «هاتف آخر»
   * يجد نفسه يتصل بغيره.
   */
  const [headerCall, setHeaderCall] = useState<string | null>(null);
  const customFields = useCustomFields("LEAD");

  const lead = useQuery({
    queryKey: ["lead", id],
    queryFn: () => api<Lead>(`/crm/leads/${id}`),
  });

  /*
   * الإرسال المباشر عبر قنوات ميتا — من الملف بلا رحلة إلى الصندوق.
   *
   * واتساب: المحادثة القائمة بالرقم تُلتقط، وإلا بُدئت على أول قناة
   * واتساب نشطة — فالرسالة تصل فورًا وتُسجَّل في المحادثة. أما ماسنجر
   * وإنستغرام فالمنصّة تمنع بدء محادثة؛ يُرسَل في محادثةٍ قائمة مربوطة
   * بهذا العميل، وإلا قيل ذلك بصراحة.
   */
  const sendQuick = useMutation({
    mutationFn: async ({
      channel,
      activity,
      text,
      file,
    }: {
      channel: QuickChannel;
      activity: Activity | null;
      text: string;
      file: File | null;
    }) => {
      const kind = channel;

      /* SMS: تُسلَّم لتطبيق الجهاز بنصّها — ولا تُعلَّم «أُرسلت» زورًا. */
      if (kind === "SMS") {
        const num =
          lead.data?.smsPhone === "altPhone"
            ? (lead.data.altPhone ?? lead.data.phone)
            : (lead.data?.phone ?? lead.data?.altPhone);
        if (!num) throw new Error("لا رقم مسجَّل لهذا العميل.");
        window.location.href = `sms:${num}?body=${encodeURIComponent(text)}`;
        return "sms-handoff" as const;
      }

      let convId: string | null = null;

      if (kind === "WHATSAPP") {
        /* الرقم الموجَّه لواتساب تحديدًا — لا الأول أيًّا كان. */
        const source =
          lead.data?.waPhone === "altPhone"
            ? (lead.data.altPhone ?? lead.data.phone)
            : (lead.data?.phone ?? lead.data?.altPhone);
        const wa = whatsappNumber(source ?? null);
        if (!wa) throw new Error("يلزم رقم هاتف بمفتاح دولة لإرسال واتساب.");
        const found = await api<{ conversationId: string | null }>(
          `/meta/by-phone${qs({ phone: wa })}`,
        );
        convId = found.conversationId;
        if (!convId) {
          const channels =
            await api<Array<{ id: string; kind: string; isActive: boolean }>>(
              "/meta/channels",
            );
          const ch = channels.find((c) => c.kind === "WHATSAPP" && c.isActive);
          if (!ch)
            throw new Error(
              "لا قناة واتساب مربوطة — اربطها من الإعدادات أولًا.",
            );
          const conv = await api<{ id: string }>("/meta/conversations", {
            method: "POST",
            body: { channelId: ch.id, phone: wa, leadId: id },
          });
          convId = conv.id;
        }
      } else {
        const convs = await api<
          Array<{ id: string; leadId: string | null; channelKind: string }>
        >("/meta/conversations");
        const conv = convs.find(
          (c) => c.leadId === id && c.channelKind === kind,
        );
        if (!conv) {
          const label = kind === "MESSENGER" ? "ماسنجر" : "إنستغرام";
          throw new Error(
            `لا محادثة ${label} مربوطة بهذا العميل بعد — المنصّة لا تسمح ببدء محادثة من طرفنا. حين يراسلكم تُربط محادثته ويُرسَل من هنا مباشرةً.`,
          );
        }
        convId = conv.id;
      }

      /*
       * الوسيط عبر مساره والنص عبر مساره — وحالة «فشلت» تُرمى بسببها:
       * الردّ 200 والرسالة محفوظة فاشلةً معناه أن العميل لم يستلم شيئًا.
       */
      let view: { status: string; error: string | null };
      if (file) {
        const form = new FormData();
        form.append("file", file);
        if (text) form.append("caption", text);
        view = await upload(`/meta/conversations/${convId}/media`, form);
      } else {
        view = await api(`/meta/conversations/${convId}/messages`, {
          method: "POST",
          body: { body: text },
        });
      }
      if (view.status === "FAILED") {
        throw new Error(view.error ?? "فشل الإرسال.");
      }

      /* إرسالٌ من صفّ نشاطٍ يُكمله — ومن زرّ الرأس رسالةٌ حرّة بلا نشاط. */
      if (activity) {
        await api(`/crm/activities/${activity.id}/complete`, {
          method: "POST",
          body: { outcome: "أُرسلت من ملف العميل عبر قنوات ميتا." },
        });
      }
      return "sent" as const;
    },
    onSuccess: (kind) => {
      setQuickMsg(null);
      toast(
        kind === "sms-handoff"
          ? "فُتح تطبيق الرسائل بنصّك — علّم النشاط منجَزًا بعد الإرسال"
          : "أُرسلت وسُجّلت في محادثتها — والنشاط اكتمل",
      );
      void queryClient.invalidateQueries({ queryKey: ["activities"] });
      void queryClient.invalidateQueries({ queryKey: ["meta"] });
      void queryClient.invalidateQueries({ queryKey: ["timeline"] });
    },
  });

  const activities = useQuery({
    queryKey: ["activities", { leadId: id }],
    queryFn: () =>
      api<{ items: Activity[] }>(
        `/crm/activities${qs({ leadId: id, pageSize: 100 })}`,
      ),
  });

  const changeStatus = useMutation({
    mutationFn: (status: LeadStatus) =>
      api(`/crm/leads/${id}`, { method: "PATCH", body: { status } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["lead", id] });
      void queryClient.invalidateQueries({ queryKey: ["leads"] });
      /* تغيّر الحالة حدثٌ في الخطّ الزمني، فيُعاد جلبه ليظهر فورًا. */
      void queryClient.invalidateQueries({ queryKey: ["timeline"] });
    },
  });

  /* التصنيف يُبدَّل من البيانات مباشرة — بارد ودافئ وساخن حبّاتٍ تُنقر. */
  const changeRating = useMutation({
    mutationFn: (rating: string) =>
      api(`/crm/leads/${id}`, { method: "PATCH", body: { rating } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["lead", id] });
      void queryClient.invalidateQueries({ queryKey: ["leads"] });
    },
  });

  /*
   * ملاحظات الفريق تُستدعى هنا أيضًا — لا لعرض القائمة بل لخلاصة
   * «آخر ملاحظة للفريق» في البيانات. المفتاح مفتاح مكوّن الملاحظات
   * نفسه فلا طلبَ مكرّرًا: الكاش واحد.
   */
  const teamFeedback = useQuery({
    queryKey: ["feedback", "lead", id],
    queryFn: () =>
      api<FeedbackView[]>(`/feedback${qs({ entity: "lead", entityId: id })}`),
    enabled: Boolean(id),
  });
  const { byId: teamById } = useTeamDirectory();

  if (lead.isError)
    return (
      <div className="page">
        <ErrorState error={lead.error} />
      </div>
    );

  if (lead.isPending) {
    return (
      <div className="page">
        <div
          className="skeleton"
          style={{ height: 28, width: 240, marginBottom: 20 }}
        />
        <div className="skeleton" style={{ height: 180 }} />
      </div>
    );
  }

  const record = lead.data;
  const converted = record.status === "CONVERTED";
  const statusDef = LEAD_STATUS_MAP[record.status];
  /* لكل قناةٍ رقمُها الموجَّه — والآخر احتياطًا حين يخلو. */
  const phoneFor = (which: "phone" | "altPhone") =>
    which === "altPhone"
      ? (record.altPhone ?? record.phone)
      : (record.phone ?? record.altPhone);
  const waSource = phoneFor(record.waPhone);
  const smsSource = phoneFor(record.smsPhone);

  return (
    <div className="page">
      {/*
        الترويسة تلتصق عند التمرير.

        الملف طويل — سجل وخطّ زمني وملاحظات ومرفقات — ومن نزل إلى آخره
        ليقرأ ما جرى هو أحوج ما يكون إلى «اتصل» و«حوّل» و«مؤهَّل». وكان
        عليه أن يصعد إلى أعلى الصفحة ليجدها.
      */}
      <div className="record-head sticky-bar">
        <PageHead
          title={record.name}
          subtitle={
            [record.jobTitle, record.company].filter(Boolean).join(" — ") ||
            undefined
          }
          avatar={
            <Avatar name={record.name} src={record.avatarUrl} size={44} />
          }
          actions={
            <>
              {/*
                قنوات التواصل أزرارًا من عائلة الأزرار نفسها — لا حبوبًا
                غريبة الشكل بجوارها. والرسائل تفتح المُرسِل الراسي مباشرةً
                بهيئة قناته: واتساب يُرسل عبر القناة المربوطة، وSMS تُكتب
                هنا ثم تُسلَّم لتطبيق الجهاز.
              */}
              <button
                className="btn btn-sm btn-icon"
                disabled={!record.phone}
                onClick={() => record.phone && setHeaderCall(record.phone)}
                title={
                  record.phone ? `اتصال — ${record.phone}` : "لا رقم مسجَّل"
                }
                aria-label="اتصال"
              >
                <Icon name="phone" size={15} />
              </button>
              <button
                className="btn btn-sm btn-icon"
                disabled={!whatsappNumber(waSource)}
                onClick={() =>
                  setQuickMsg({ channel: "WHATSAPP", activity: null })
                }
                title={
                  whatsappNumber(waSource)
                    ? "رسالة واتساب — تُرسَل مباشرة"
                    : "يلزم رقم بمفتاح دولة"
                }
                aria-label="واتساب"
              >
                <Icon name="chat" size={15} />
              </button>
              <button
                className="btn btn-sm btn-icon"
                disabled={!smsSource}
                onClick={() => setQuickMsg({ channel: "SMS", activity: null })}
                title={
                  smsSource
                    ? "رسالة SMS — تُكتب هنا وتُرسَل من جهازك"
                    : "لا رقم مسجَّل"
                }
                aria-label="SMS"
              >
                <Icon name="sms" size={15} />
              </button>
              <button
                className="btn btn-sm btn-icon"
                disabled={!record.email}
                onClick={() => setHeaderMail(true)}
                title={
                  record.email
                    ? `راسل ${record.email} من صندوق الشركة`
                    : "لا بريد مسجَّل"
                }
                aria-label="بريد"
              >
                <Icon name="mail" size={15} />
              </button>

              {/* المؤقّت هنا: يُبدأ حين يبدأ العمل عليه لا بعد أن يُبحث عنه. */}
              <TimerButton
                entity="lead"
                entityId={id}
                className="btn btn-sm btn-ghost"
              />

              <Link to="/crm/leads" className="btn btn-sm">
                <Icon name="arrowLeft" size={15} />
                رجوع
              </Link>
              <button className="btn btn-sm" onClick={() => setEditing(true)}>
                <Icon name="edit" size={15} />
                تعديل
              </button>
              {!converted && (
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => setConverting(true)}
                >
                  تحويل إلى عميل
                </button>
              )}
              <DeleteButton
                entity="lead"
                id={id}
                endpoint={`/crm/leads/${id}`}
                onDeleted={() => navigate("/crm/leads")}
              />
              {/* تغيير النمط: الأقسام تُسحب وتُخفى وتُكبَّر بأمر صاحب الشاشة. */}
              <ProfileLayoutButton api={layout} />
              {/* «اختر موعدك» — رابطٌ يُرسل فيحجز العميل بنفسه. */}
              <BookingLinkButton
                className="btn btn-sm"
                email={record.email}
                link={{ leadId: id }}
              />
              {/* منتقي البطاقات في الرأس بجوار النقاط — لا زرًّا يُبحث عنه تحت. */}
              <CardPickerButton
                choices={leadCards(record, activities.data?.items ?? [])}
                chosen={cardChoice.chosen}
                onChange={cardChoice.setChosen}
                onReset={cardChoice.reset}
                className="btn btn-sm"
              />
              {/* النقاط محدودةٌ كإخوتها — الشبحية كانت تُقرأ زخرفةً لا زرًّا. */}
              <LeadQuickActions
                lead={record}
                showOpen={false}
                className="btn btn-sm btn-icon"
                onChanged={() => {
                  void queryClient.invalidateQueries({
                    queryKey: ["lead", id],
                  });
                  void queryClient.invalidateQueries({
                    queryKey: ["activities"],
                  });
                  void queryClient.invalidateQueries({
                    queryKey: ["timeline"],
                  });
                }}
              />
            </>
          }
        />

        {/*
          شريط المراحل ميترًا: كل الخلايا من «جديد» حتى الحالية تُعبَّأ
          بتدرّجٍ متصلٍ يبدأ أزرق وينتهي بلون المرحلة — أخضر عند مؤهّل،
          وأحمر خفيف عند غير مؤهّل، وصريح عند مستبعد. فالنظرة الواحدة
          تقول أين وصل العميل وبأي مزاج.
        */}
        <div className="stagebar">
          {(() => {
            const defs = LEAD_STATUS_DEFS.filter((s) => s.key !== "CONVERTED");
            const idx = defs.findIndex((s) => s.key === record.status);
            const end = STAGE_METER_END[record.status] ?? STAGE_METER_START;
            return defs.map((s, j) => {
              const filled = idx >= 0 && j <= idx;
              const isCurrent = s.key === record.status;
              const bg = filled
                ? lerpHex(STAGE_METER_START, end, idx === 0 ? 1 : j / idx)
                : undefined;
              return (
                <button
                  key={s.key}
                  className={`stage-step${isCurrent ? " current" : filled ? " done" : ""}`}
                  style={bg ? { background: bg, borderColor: bg } : undefined}
                  onClick={() => changeStatus.mutate(s.key)}
                  disabled={converted || changeStatus.isPending}
                  title={
                    converted ? "العميل محوَّل ولا تُعدَّل حالته" : undefined
                  }
                >
                  {s.label}
                </button>
              );
            });
          })()}
        </div>
      </div>

      <LeadSnapshot
        record={record}
        activities={activities.data?.items ?? []}
        external={cardChoice}
      />

      {converted && record.convertedAccountId && (
        <div className="alert alert-info mb-4">
          <Icon name="check" size={18} />
          <div>
            تم تحويل هذا العميل المحتمل إلى عميل.{" "}
            <Link to={`/crm/accounts/${record.convertedAccountId}`}>
              فتح ملف العميل
            </Link>
          </div>
        </div>
      )}

      <ProfileLayoutProvider api={layout}>
        <ProfileLayoutBar api={layout} />
        <div className="two-col">
          <LayoutColumn api={layout} column="main">
            {/*
            سجل التعامل مكوّنًا مشتركًا — هذا الملف وملف العميل سواء
            شكلًا وخياراتٍ وسلوكًا. ميتر التصنيف يُمرَّر من هنا لأنه
            شأن العميل المحتمل وحده.
          */}
            <Section layoutKey="hub">
              <ActivityHub
                entity="lead"
                record={{
                  id,
                  name: record.name,
                  phone: record.phone,
                  altPhone: record.altPhone,
                  email: record.email,
                  waPhone: record.waPhone,
                  smsPhone: record.smsPhone,
                  mapsUrl: record.mapsUrl,
                }}
                tabsExtra={
                  <RatingMeter
                    rating={record.rating}
                    pending={changeRating.isPending}
                    onChange={(r) => changeRating.mutate(r)}
                  />
                }
              />
            </Section>

            {/*
            الصفقاتُ من ملف المحتمل — وهي وثائقُ على حساب.

            المحتملُ الذي تحوّل تُقرأ صفقاتُه هنا كما هي في ملف حسابه. والذي
            لم يتحوّل بعد يُقال له: الصفقةُ تُفتح بعد التحويل — فردًا أو
            شركةً — وزرُّ التحويل بين يديه.
          */}
            <Section layoutKey="deals">
              <LeadDeals
                record={{
                  id,
                  status: record.status,
                  convertedAccountId: record.convertedAccountId,
                }}
                onConvert={() => setConverting(true)}

                onQualify={(quotationId) => {
                  setPendingAccept(quotationId);

                  setConverting(true);
                }}
              />
            </Section>

            <Section layoutKey="feedback">
              <Feedback entity="lead" entityId={id} title="ملاحظات الفريق" />
            </Section>

            <Section layoutKey="attachments">
              <Attachments
                entity="lead"
                entityId={id}
                title="مرفقات العميل المحتمل"
              />
            </Section>
          </LayoutColumn>

          <LayoutColumn api={layout} column="side">
            <Section layoutKey="data">
              <div className="card">
                <div className="card-head">
                  <h2 className="card-title">البيانات</h2>
                </div>
                <div className="card-body">
                  {/*
                الحالة شارةً، والتصنيف معلومةً تُقرأ لا زرًّا: بارد ودافئ
                وساخن ظاهرةً والحاليّة ملوّنة — والتبديل من ميتر التصنيف
                بجوار تبويبات سجل التعامل.
              */}
                  <div className="ld-badges">
                    <Badge kind={statusDef?.kind}>{statusDef?.label}</Badge>
                    <span className="ld-rate" aria-label="الأولوية">
                      {[...LEAD_RATING_DEFS].reverse().map((r) => (
                        <span
                          key={r.key}
                          className={`ld-rate-btn${
                            record.rating === r.key
                              ? ` on rate-${r.key.toLowerCase()}`
                              : ""
                          }`}
                        >
                          {r.label}
                        </span>
                      ))}
                    </span>
                    {record.value ? (
                      <span className="ld-worth num">
                        {money(record.value)}
                      </span>
                    ) : null}
                  </div>

                  {/*
                التواصل صفوفًا بأيقوناتها: الرقم يُتّصل به، والبريد
                يُراسَل، والسوشيال «@اسم» بأيقونته لا رابطًا يلتفّ —
                وقنوات التوجيه وسومٌ على رقمها.
              */}
                  <div className="ld-rows">
                    <div className="ld-row">
                      <span className="ld-ico">
                        <Icon name="phone" size={14} />
                      </span>
                      <span className="ld-label">الهاتف</span>
                      <span className="ld-value num">
                        {record.phone ? (
                          /* الاتصال في مكانه — لوحةٌ راسيةٌ كسائر أزرار القنوات. */
                          <button
                            type="button"
                            className="link-btn num"
                            dir="ltr"
                            onClick={() => setHeaderCall(record.phone)}
                            title={`اتصل — ${record.phone}`}
                          >
                            {record.phone}
                          </button>
                        ) : (
                          <span className="mute">—</span>
                        )}
                      </span>
                      {record.phone &&
                        (record.waPhone === "phone" ||
                          record.smsPhone === "phone") && (
                          <span className="ld-tags">
                            {record.waPhone === "phone" && (
                              <span className="ld-tag is-wa">واتساب</span>
                            )}
                            {record.smsPhone === "phone" && (
                              <span className="ld-tag">SMS</span>
                            )}
                          </span>
                        )}
                    </div>

                    {record.altPhone && (
                      <div className="ld-row">
                        <span className="ld-ico">
                          <Icon name="phone" size={14} />
                        </span>
                        <span className="ld-label">هاتف آخر</span>
                        <span className="ld-value num">
                          <button
                            type="button"
                            className="link-btn num"
                            dir="ltr"
                            onClick={() =>
                              record.altPhone && setHeaderCall(record.altPhone)
                            }
                            title={`اتصل — ${record.altPhone}`}
                          >
                            {record.altPhone}
                          </button>
                        </span>
                        {(record.waPhone === "altPhone" ||
                          record.smsPhone === "altPhone") && (
                          <span className="ld-tags">
                            {record.waPhone === "altPhone" && (
                              <span className="ld-tag is-wa">واتساب</span>
                            )}
                            {record.smsPhone === "altPhone" && (
                              <span className="ld-tag">SMS</span>
                            )}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="ld-row">
                      <span className="ld-ico">
                        <Icon name="mail" size={14} />
                      </span>
                      <span className="ld-label">البريد</span>
                      <span className="ld-value num">
                        {record.email ? (
                          <a
                            className="link"
                            dir="ltr"
                            href={`mailto:${record.email}`}
                          >
                            {record.email}
                          </a>
                        ) : (
                          <span className="mute">—</span>
                        )}
                      </span>
                    </div>

                    {record.messengerHandle &&
                      (() => {
                        const s = socialRef(
                          record.messengerHandle,
                          "messenger",
                        );
                        return (
                          <div className="ld-row">
                            <span className="ld-ico">
                              <ChannelLogo kind="MESSENGER" size={15} />
                            </span>
                            <span className="ld-label">ماسنجر</span>
                            <span className="ld-value num">
                              <a
                                className="link"
                                dir="ltr"
                                href={s.href}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {s.handle}
                              </a>
                            </span>
                          </div>
                        );
                      })()}

                    {record.instagramHandle &&
                      (() => {
                        const s = socialRef(
                          record.instagramHandle,
                          "instagram",
                        );
                        return (
                          <div className="ld-row">
                            <span className="ld-ico">
                              <ChannelLogo kind="INSTAGRAM" size={15} />
                            </span>
                            <span className="ld-label">إنستغرام</span>
                            <span className="ld-value num">
                              <a
                                className="link"
                                dir="ltr"
                                href={s.href}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {s.handle}
                              </a>
                            </span>
                          </div>
                        );
                      })()}

                    {record.mapsUrl && (
                      <div className="ld-row">
                        <span className="ld-ico">
                          <Icon name="building" size={14} />
                        </span>
                        <span className="ld-label">موقع العميل</span>
                        <span className="ld-value">
                          <a
                            className="link"
                            href={record.mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            افتح على الخريطة
                          </a>
                        </span>
                      </div>
                    )}
                  </div>

                  <dl className="detail-list mt-4">
                    <dt>المصدر</dt>
                    <dd>{record.source ?? "—"}</dd>

                    <dt>المدينة</dt>
                    <dd>{record.city ?? "—"}</dd>

                    <dt>آخر تواصل</dt>
                    <dd>
                      {record.lastContactedAt
                        ? dateTime(record.lastContactedAt)
                        : "لم يتم"}
                    </dd>

                    <dt>أُضيف في</dt>
                    <dd>{date(record.createdAt)}</dd>

                    {/*
                  الحقول المخصَّصة داخل القائمة نفسها: هي بيانات العميل
                  المحتمل كغيرها، وفصلها يوحي بأنها شيء آخر.
                */}
                    {activeFields(customFields.data).map((def) => (
                      <Fragment key={def.key}>
                        <dt>{def.label}</dt>
                        <dd
                          className={
                            def.type === "NUMBER" || def.type === "DATE"
                              ? "num"
                              : undefined
                          }
                        >
                          <CustomValue
                            def={def}
                            raw={(record.customFields ?? {})[def.key]}
                          />
                        </dd>
                      </Fragment>
                    ))}
                  </dl>

                  {record.notes && (
                    <div className="mt-4">
                      <div className="label mb-4">ملاحظات</div>
                      <p className="small" style={{ whiteSpace: "pre-wrap" }}>
                        {record.notes}
                      </p>
                    </div>
                  )}

                  {/*
                خلاصة الصفحة: آخر ما قيل عن العميل وآخر ما قاله الفريق —
                فمن يفتح الملف يقرأ الوضع بنظرةٍ قبل أن ينزل إلى السجلات.
              */}
                  {(() => {
                    const lastNote = (activities.data?.items ?? [])
                      .filter((a) => a.type === "NOTE")
                      .sort((a, b) =>
                        (a.createdAt ?? "") < (b.createdAt ?? "") ? 1 : -1,
                      )[0];
                    if (!lastNote) return null;
                    return (
                      <div className="mt-4">
                        <div className="label mb-4">آخر ملاحظة للعميل</div>
                        {/* معلومةٌ تُقرأ في مكانها: النصّ نفسه لا عنوانٌ يُفتح. */}
                        <div className="ld-last">
                          <span className="small" style={{ fontWeight: 600 }}>
                            {lastNote.subject}
                          </span>
                          {lastNote.body && (
                            <span className="small">{lastNote.body}</span>
                          )}
                          <span className="tiny mute">
                            {dateTime(lastNote.createdAt)}
                            {lastNote.noteAbout &&
                              ACTIVITY_TYPE_MAP[
                                lastNote.noteAbout as Activity["type"]
                              ] &&
                              ` — تخصّ ${ACTIVITY_TYPE_MAP[lastNote.noteAbout as Activity["type"]]!.plural}`}
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {(() => {
                    const flat = (teamFeedback.data ?? []).flatMap((r) => [
                      r,
                      ...r.replies,
                    ]);
                    const lastFb = [...flat].sort((a, b) =>
                      a.createdAt < b.createdAt ? 1 : -1,
                    )[0];
                    if (!lastFb) return null;
                    return (
                      <div className="mt-4">
                        <div className="label mb-4">آخر ملاحظة للفريق</div>
                        <div className="ld-last">
                          <span className="small">
                            {lastFb.body
                              ? renderMentions(
                                  lastFb.body,
                                  (uid) => teamById.get(uid)?.name,
                                )
                              : "تسجيل صوتي — استمع إليه في ملاحظات الفريق"}
                          </span>
                          <span className="tiny mute">
                            {lastFb.authorName ?? "مستخدم سابق"} ·{" "}
                            {dateTime(lastFb.createdAt)}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </Section>

            {/*
            «ما جرى» تحت البيانات لا في العمود المقابل.

            السؤالان متتابعان: من هو؟ ثم ماذا جرى معه؟ وفصلُهما في عمودين
            يجعل العين تقفز بينهما. والعمود المقابل لِما يُكتب الآن — نشاطٌ
            جديد وملاحظة ومرفق.
          */}
            <Section layoutKey="timeline">
              {/* مختصرًا: آخر خمسة والباقي تمريرٌ داخلي — وزرّ التكبير للسجل كله. */}
              <RecordTimeline
                entity="lead"
                id={id}
                title="ما جرى مع هذا العميل"
                compact
              />
            </Section>

            <Section layoutKey="timelog">
              <TimeLog entity="lead" entityId={id} />
            </Section>
          </LayoutColumn>
        </div>
      </ProfileLayoutProvider>

      {editing && (
        <LeadEditor lead={record} onClose={() => setEditing(false)} />
      )}

      {/* واتساب وماسنجر وإنستغرام وSMS — مُرسِلٌ راسٍ بلون قناته. */}
      {quickMsg && (
        <QuickMessageModal
          channel={quickMsg.channel}
          clientName={record.name}
          hint={
            quickMsg.channel === "WHATSAPP"
              ? "تُرسَل عبر قناة واتساب المربوطة وتُسجَّل في محادثة العميل — والوسائط كذلك."
              : quickMsg.channel === "MESSENGER"
                ? "تُرسَل في محادثة العميل القائمة — والوسائط تُرفع عبر واجهة ماسنجر الرسمية."
                : quickMsg.channel === "SMS"
                  ? "تُفتح في تطبيق الرسائل على جهازك بنصّها — لا بوابة SMS مربوطة بعد."
                  : "تُرسَل في محادثة العميل القائمة — وإنستغرام نصٌّ فقط: واجهته لا تقبل رفع وسائط."
          }
          pending={sendQuick.isPending}
          error={
            sendQuick.error instanceof Error ? sendQuick.error.message : null
          }
          onSend={(text, file) =>
            sendQuick.mutate({
              channel: quickMsg.channel,
              activity: quickMsg.activity,
              text,
              file,
            })
          }
          onClose={() => {
            sendQuick.reset();
            setQuickMsg(null);
          }}
        />
      )}

      {/* اتصال الرأس — لوحة الكونسول راسيةً في الملف: تبدأ وتُنهى هنا. */}
      {headerCall && (
        <QuickCallModal
          clientName={record.name}
          phone={headerCall}
          link={{ leadId: id }}
          onClose={() => {
            setHeaderCall(null);
            void queryClient.invalidateQueries({ queryKey: ["activities"] });
            void queryClient.invalidateQueries({ queryKey: ["timeline"] });
          }}
        />
      )}

      {/* بريد الرأس — رسالة حرّة من صندوق الشركة بلا نشاطٍ تُكمله. */}
      {headerMail && record.email && (
        <MailCompose
          to={record.email}
          link={{ leadId: id }}
          onClose={() => setHeaderMail(false)}
          onSent={() => {
            setHeaderMail(false);
            void queryClient.invalidateQueries({ queryKey: ["timeline"] });
          }}
        />
      )}

      {converting && (
        <ConvertModal
          lead={record}
          onClose={() => setConverting(false)}
          onDone={(accountId) => {
            void (async () => {
              if (pendingAccept) {
                try {
                  await api(`/sales/quotations/${pendingAccept}/decide`, {
                    method: "POST",
                    body: { decision: "ACCEPTED" },
                  });
                  toast("حُوّل إلى عميل وسُجّلت موافقته على العرض");
                } catch (e) {
                  toast(
                    e instanceof Error
                      ? e.message
                      : "حُوّل — وتعذّر تسجيل الموافقة، سجّلها من العرض.",
                  );
                }
                setPendingAccept(null);
              }
              void queryClient.invalidateQueries();
              navigate(`/crm/accounts/${accountId}`);
            })();
          }}
        />
      )}
    </div>
  );
}

/* ==========================================================================
   نافذة التحويل
   ========================================================================== */

/**
 * تحويل العميل المحتمل إلى حساب.
 *
 * ثلاثة قرارات يتخذها المستخدم هنا، وكانت مفروضة عليه قبل ذلك:
 *
 *   1. نوع الحساب: فرد أم شركة. الاشتقاق من وجود اسم شركة تخمين يخطئ كثيرًا،
 *      فكثير من الأفراد يذكرون جهة عملهم دون أن يكونوا عميلًا مؤسسيًا.
 *   2. الربط بحساب قائم بدل إنشاء جديد، وهو ما يمنع تكرار العميل نفسه.
 *   3. إنشاء صفقة من عدمه وقيمتها.
 */
function ConvertModal({
  lead,
  onClose,
  onDone,
}: {
  lead: Lead;
  onClose: () => void;
  onDone: (accountId: string) => void;
}) {
  const [mode, setMode] = useState<"new" | "existing">("new");
  const [accountKind, setAccountKind] = useState<AccountKind>(
    lead.company ? "COMPANY" : "INDIVIDUAL",
  );
  const [accountName, setAccountName] = useState(lead.company ?? lead.name);
  const [existingAccountId, setExistingAccountId] = useState("");
  const [createDeal, setCreateDeal] = useState(true);
  const [dealTitle, setDealTitle] = useState(
    `صفقة ${lead.company ?? lead.name}`,
  );
  const [dealValue, setDealValue] = useState(lead.value ?? "");

  const accounts = useQuery({
    queryKey: ["accounts", "options"],
    queryFn: () =>
      api<{ items: Array<{ id: string; name: string; kind: AccountKind }> }>(
        `/crm/accounts${qs({ pageSize: 100, sort: "name", order: "asc" })}`,
      ),
    enabled: mode === "existing",
  });

  const convert = useMutation({
    mutationFn: () =>
      api<{ accountId: string }>(`/crm/leads/${lead.id}/convert`, {
        method: "POST",
        body: {
          ...(mode === "existing"
            ? { existingAccountId }
            : { accountKind, accountName }),
          createDeal,
          dealTitle: createDeal ? dealTitle : undefined,
          dealValue: createDeal && dealValue ? Number(dealValue) : undefined,
        },
      }),
    onSuccess: (res) => onDone(res.accountId),
  });

  const valid =
    mode === "existing" ? !!existingAccountId : accountName.trim().length >= 2;

  return (
    <Modal
      title="تحويل إلى عميل"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => convert.mutate()}
            disabled={!valid || convert.isPending}
          >
            {convert.isPending ? "جارٍ التحويل" : "تحويل"}
          </button>
        </>
      }
    >
      {convert.isError && (
        <div className="alert alert-danger mb-4">
          {convert.error instanceof Error
            ? convert.error.message
            : "تعذّر التحويل."}
        </div>
      )}

      <div className="tabs" role="tablist" aria-label="وجهة التحويل">
        <button
          className="tab"
          role="tab"
          aria-selected={mode === "new"}
          onClick={() => setMode("new")}
        >
          حساب جديد
        </button>
        <button
          className="tab"
          role="tab"
          aria-selected={mode === "existing"}
          onClick={() => setMode("existing")}
        >
          ربط بحساب قائم
        </button>
      </div>

      <div className="col">
        {mode === "new" ? (
          <>
            <div className="field">
              <span className="label">نوع الحساب</span>
              <div className="row" style={{ gap: 2 }}>
                {ACCOUNT_KIND_DEFS.map((k) => (
                  <button
                    key={k.key}
                    type="button"
                    className={`btn btn-sm${accountKind === k.key ? " btn-primary" : ""}`}
                    onClick={() => setAccountKind(k.key)}
                    aria-pressed={accountKind === k.key}
                  >
                    {k.label}
                  </button>
                ))}
              </div>
              <span className="tiny mute">
                {accountKind === "COMPANY"
                  ? "ستُنشأ جهة اتصال رئيسية باسم العميل تحت الحساب."
                  : "الحساب الفردي هو الشخص نفسه، فلا تُنشأ جهة اتصال منفصلة."}
              </span>
            </div>

            <div className="field">
              <label className="label req" htmlFor="c-name">
                اسم الحساب
              </label>
              <input
                id="c-name"
                className="input"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
              />
              {lead.company && accountKind === "INDIVIDUAL" && (
                <span className="tiny mute">
                  العميل مسجَّل لديه شركة ({lead.company}). تأكد أن الحساب
                  الفردي هو المقصود.
                </span>
              )}
            </div>
          </>
        ) : (
          <div className="field">
            <label className="label req" htmlFor="c-existing">
              الحساب القائم
            </label>
            <select
              id="c-existing"
              className="select"
              value={existingAccountId}
              onChange={(e) => setExistingAccountId(e.target.value)}
            >
              <option value="">اختر حسابًا</option>
              {accounts.data?.items.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} — {ACCOUNT_KIND_MAP[a.kind]?.label}
                </option>
              ))}
            </select>
            <span className="tiny mute">
              يُستخدم عند تكرار العميل: تُنقل أنشطته إلى الحساب القائم بدل إنشاء
              نسخة ثانية.
            </span>
          </div>
        )}

        <label className="row" style={{ cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={createDeal}
            onChange={(e) => setCreateDeal(e.target.checked)}
          />
          <span>إنشاء صفقة مرتبطة</span>
        </label>

        {createDeal && (
          <div className="form-grid">
            <div className="field">
              <label className="label" htmlFor="c-deal-title">
                عنوان الصفقة
              </label>
              <input
                id="c-deal-title"
                className="input"
                value={dealTitle}
                onChange={(e) => setDealTitle(e.target.value)}
              />
            </div>
            <div className="field">
              <label className="label" htmlFor="c-value">
                قيمة الصفقة
              </label>
              <input
                id="c-value"
                className="input num"
                type="number"
                min="0"
                value={dealValue}
                onChange={(e) => setDealValue(e.target.value)}
              />
            </div>
          </div>
        )}

        <p className="tiny mute">
          تُنقل أنشطة العميل المحتمل إلى الحساب والصفقة، فيبقى تاريخ التعامل
          متصلًا.
        </p>
      </div>
    </Modal>
  );
}

/* ==========================================================================
   الملخّص السريع
   ========================================================================== */

/** يوم كامل بالمللي ثانية. */
const DAY = 86_400_000;

/**
 * ما يريد المندوب معرفته قبل أن يرفع السماعة.
 *
 * الأعداد محسوبة من الأنشطة المجلوبة أصلًا لهذه الشاشة، لا بطلب إحصاء
 * مستقل: السجل الواحد أنشطته عشرات، وعدّها في المتصفح أرخص من رحلة إلى
 * الخادم تعود بالأرقام نفسها.
 *
 * و«منذ آخر تواصل» يُعرض بالأيام لا بتاريخٍ يُطرح ذهنيًا: الرقم هو ما
 * يُتّخذ عليه القرار، والتاريخ تفصيل يليه.
 */
/**
 * قيمة حقلٍ مخصص بنوعها: الرابط يُنقر وبأيقونة السوشيال حين يكون منها،
 * والهاتف باتجاهه، والبريد يُراسَل بنقرة — لا نصوصٌ تُنسخ باليد.
 */
function CustomValue({ def, raw }: { def: { type: string }; raw: unknown }) {
  if (raw === null || raw === undefined || raw === "") return <>—</>;
  if (def.type === "BOOLEAN") return <>{raw === true ? "نعم" : "لا"}</>;

  const text = String(raw);

  if (def.type === "PHONE") {
    return (
      <span className="num" dir="ltr">
        {text}
      </span>
    );
  }

  if (def.type === "EMAIL") {
    return (
      <a className="link num" dir="ltr" href={`mailto:${text}`}>
        {text}
      </a>
    );
  }

  if (def.type === "URL") {
    const href = /^https?:\/\//i.test(text) ? text : `https://${text}`;
    const host = href.toLowerCase();
    const kind = host.includes("instagram.com")
      ? ("INSTAGRAM" as const)
      : host.includes("m.me") ||
          host.includes("messenger.com") ||
          host.includes("facebook.com")
        ? ("MESSENGER" as const)
        : host.includes("wa.me") || host.includes("whatsapp.com")
          ? ("WHATSAPP" as const)
          : null;
    return (
      <a
        className="row link num"
        style={{ gap: 6, display: "inline-flex" }}
        dir="ltr"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {kind ? (
          <ChannelLogo kind={kind} size={15} />
        ) : (
          <Icon name="link" size={13} />
        )}
        {text}
      </a>
    );
  }

  return <>{text}</>;
}

/**
 * بطاقات الملف دالةٌ لا مكوّن: الرأس يحتاج قائمتها لمنتقيه، وصفّ
 * البطاقات يحتاجها للعرض — والحسابُ واحدٌ فلا يفترق الاسم عن بطاقته.
 */
function leadCards(record: Lead, activities: Activity[]): RecordStat[] {
  const count = (type: Activity["type"]): number =>
    activities.filter((a) => a.type === type).length;

  /*
   * القادم هو أقرب نشاط مخطَّط لم يحن بعد — لا أقدمُ فائت.
   *
   * كان أقدمُ المتأخرات يحتلّ البطاقة للأبد فتبدو معلومةً قديمة: الآن
   * أقربُ موعدٍ لم يفت يتقدّم، ولا يُعرض الفائت (بوسمه الأحمر) إلا حين
   * لا قادمَ سواه. و`nextActionAt` على السجل أُهمل أصلًا: الأنشطة أصدق.
   */
  const plannedWithDue = activities
    .filter((a) => a.status === "PLANNED" && a.dueAt)
    .map((a) => ({ a, at: new Date(a.dueAt!).getTime() }))
    .filter((x) => Number.isFinite(x.at))
    .sort((x, y) => x.at - y.at);
  const upcoming =
    plannedWithDue.find((x) => x.at >= Date.now()) ?? plannedWithDue[0];

  const overdue = upcoming ? upcoming.at < Date.now() : false;

  /* أحدث ما سُجّل في الملف أيًّا كان نوعه — بطاقة «آخر نشاط». */
  const lastActivity = [...activities].sort((a, b) =>
    (a.createdAt ?? "") < (b.createdAt ?? "") ? 1 : -1,
  )[0];

  const sinceDays = record.lastContactedAt
    ? Math.floor(
        (Date.now() - new Date(record.lastContactedAt).getTime()) / DAY,
      )
    : null;

  const planned = activities.filter((a) => a.status === "PLANNED").length;

  const ageDays = Math.floor(
    (Date.now() - new Date(record.createdAt).getTime()) / DAY,
  );

  /*
   * كل ما تعرفه الشاشة، لا ما نظنّه مهمًّا.
   *
   * المندوب الذي يبيع بالهاتف يريد «آخر تواصل» و«المكالمات»، ومن يتابع
   * جودة المصادر يريد «المصدر» و«التصنيف»، ومن يوزّع المتابعة يريد
   * «المسؤول» و«عمر السجل». وثلاثتهم يفتحون الشاشة نفسها.
   *
   * فتُعرَض جميعًا في المُنتقي، ويظهر منها ما يختاره كلٌّ منهم.
   */
  const cards: RecordStat[] = [
    {
      key: "owner",
      label: "المسؤول",
      group: "المتابعة",
      icon: "user",
      color: "#3672EA",
      value: <OwnerChip ownerId={record.ownerId} size={26} />,
      note: record.ownerId ? undefined : "لم يُسنَد بعد",
      hint: "من يتابع هذا العميل المحتمل.",
    },
    {
      key: "lastContact",
      label: "آخر تواصل",
      group: "المتابعة",
      icon: "clock",
      color: sinceDays !== null && sinceDays > 14 ? "#DC2626" : "#5596FF",
      value:
        sinceDays === null
          ? "لم يتم"
          : /* الصفر ليس «٠ يومًا»: من تواصل اليوم يقرأ «اليوم». */
            sinceDays === 0
            ? "اليوم"
            : `منذ ${plural(sinceDays, WORDS.day)}`,
      note: record.lastContactedAt
        ? date(record.lastContactedAt)
        : "لا تواصل مسجَّل",
      hint: "كم مضى منذ آخر نشاط مسجَّل معه.",
    },
    {
      key: "lastActivity",
      label: "آخر نشاط",
      group: "المتابعة",
      icon: "zap",
      color: "#5596FF",
      value: lastActivity ? lastActivity.subject : "لا نشاط بعد",
      note: lastActivity
        ? `${ACTIVITY_TYPE_MAP[lastActivity.type]?.label ?? ""} · ${dateTime(lastActivity.createdAt)}`
        : "ابدأ بأول تواصل",
      hint: "أحدث ما سُجّل في الملف — أيًّا كان نوعه.",
    },
    {
      key: "next",
      label: "النشاط القادم",
      group: "المتابعة",
      icon: "calendar",
      color: overdue ? "#DC2626" : "#51A521",
      value: upcoming ? upcoming.a.subject : "لا شيء مجدول",
      note: upcoming
        ? `${overdue ? "فات موعده — " : ""}${dateTime(upcoming.a.dueAt)}`
        : "جدول متابعة حتى لا يُنسى",
      hint: "أقرب نشاط مخطَّط — والأنشطة أصدق من تاريخٍ مسجَّل على السجلّ.",
    },
    {
      key: "openTasks",
      label: "مهامّ مفتوحة",
      group: "المتابعة",
      icon: "clipboard",
      color: planned > 0 ? "#BF930B" : "#9BA3AE",
      value: planned,
      numeric: true,
      note: planned === 0 ? "لا شيء معلَّق" : "لم تُنجَز بعد",
      hint: "الأنشطة المخطَّطة التي لم تُغلق.",
    },
    {
      key: "age",
      label: "عمر السجل",
      group: "المتابعة",
      icon: "plus",
      color: "#8791A1",
      value: ageDays === 0 ? "اليوم" : plural(ageDays, WORDS.day),
      note: date(record.createdAt),
      hint: "منذ متى دخل هذا العميل المحتمل النظام.",
    },

    {
      key: "touches",
      label: "التعامل",
      group: "التعامل",
      icon: "chat",
      color: "#1D46A6",
      value: `${count("CALL")} / ${count("MEETING")} / ${count("MAIL")}`,
      numeric: true,
      note: "مكالمات / اجتماعات / رسائل",
      hint: "مجمل ما جرى معه، موزَّعًا على القنوات.",
    },
    {
      key: "calls",
      label: "المكالمات",
      group: "التعامل",
      icon: "phone",
      color: "#51A521",
      value: count("CALL"),
      numeric: true,
      hint: "عدد المكالمات المسجَّلة.",
    },
    {
      key: "meetings",
      label: "الاجتماعات",
      group: "التعامل",
      icon: "video",
      color: "#5596FF",
      value: count("MEETING"),
      numeric: true,
      hint: "عدد الاجتماعات المسجَّلة.",
    },
    {
      key: "mails",
      label: "الرسائل",
      group: "التعامل",
      icon: "mail",
      color: "#BF930B",
      value: count("MAIL"),
      numeric: true,
      hint: "عدد الرسائل البريدية المسجَّلة.",
    },
    {
      key: "activities",
      label: "إجمالي الأنشطة",
      group: "التعامل",
      icon: "layers",
      color: "#6F7784",
      value: activities.length,
      numeric: true,
      note: activities.length === 0 ? "لم يُسجَّل شيء" : undefined,
      hint: "كل ما سُجِّل على هذا السجلّ.",
    },

    {
      key: "value",
      label: "القيمة المتوقعة",
      group: "التصنيف",
      icon: "wallet",
      color: "#51A521",
      value: record.value ? money(record.value) : "—",
      numeric: !!record.value,
      hint: "ما نتوقّع أن تبلغه الصفقة إن نجحت.",
    },
    {
      key: "status",
      label: "الحالة",
      group: "التصنيف",
      icon: "funnel",
      color: "#3672EA",
      value: LEAD_STATUS_MAP[record.status]?.label ?? record.status,
      note: record.convertedAt
        ? `حُوِّل في ${date(record.convertedAt)}`
        : undefined,
      hint: "موضعه في مسار التأهيل.",
    },
    {
      key: "rating",
      label: "الأولوية",
      group: "التصنيف",
      icon: "zap",
      color:
        record.rating === "HOT"
          ? "#DC2626"
          : record.rating === "WARM"
            ? "#DDBC10"
            : "#5596FF",
      value: LEAD_RATING_MAP[record.rating]?.label ?? record.rating,
      hint: "أولوية المتابعة كما قدّرها المندوب.",
    },
    {
      key: "source",
      label: "المصدر",
      group: "التصنيف",
      icon: "arrowRightLeft",
      color: "#8791A1",
      value: record.source || "—",
      note: record.source ? undefined : "غير مسجَّل",
      hint: "من أين جاء — وهو ما يُقاس عليه أداء القنوات.",
    },

    {
      key: "company",
      label: "الشركة",
      group: "البيانات",
      icon: "building",
      color: "#1D46A6",
      value: record.company || "—",
      note: record.jobTitle || undefined,
      hint: "الجهة التي يعمل بها.",
    },
    {
      key: "city",
      label: "المدينة",
      group: "البيانات",
      icon: "home",
      color: "#6F7784",
      value: record.city || "—",
      note: record.address || undefined,
      hint: "موقعه — ويهمّ من يوزّع المندوبين جغرافيًّا.",
    },
    {
      key: "industry",
      label: "النشاط",
      group: "البيانات",
      icon: "chartBar",
      color: "#9BA3AE",
      value: record.industry || "—",
      hint: "قطاع عمله.",
    },
  ];

  return cards;
}

function LeadSnapshot({
  record,
  activities,
  external,
}: {
  record: Lead;
  activities: Activity[];
  external?: {
    chosen: string[];
    setChosen: (keys: string[]) => void;
    reset: () => void;
  };
}) {
  return (
    <RecordStats
      items={leadCards(record, activities)}
      storageKey="goyone.stats.lead"
      defaults={LEAD_CARD_DEFAULTS}
      /* المنتقي في رأس الصفحة — فلا يتكرّر هنا. */
      external={external}
    />
  );
}

/* ==========================================================================
   الصفقات — من ملف العميل المحتمل
   ========================================================================== */

function LeadDeals({
  record,
  onConvert,
  onQualify,
}: {
  record: { id: string; status: string; convertedAccountId: string | null };
  onConvert: () => void;
  /** قبِل عرضًا وهو محتمل — يُحوَّل ثم تُسجَّل موافقتُه. */
  onQualify: (quotationId: string) => void;
}) {
  const navigate = useNavigate();
  const accountId = record.convertedAccountId;
  /*
   * اللوحةُ نفسُها التي في ملف العميل — أعمدةٌ وبطاقاتٌ وخطوةٌ تالية.
   *
   * العروضُ على المحتمل نفسِه قبل تحويله، وعلى حسابه بعده. وقبولُ عرضٍ
   * وصاحبُه محتملٌ بعد يفتح نافذةَ التحويل من هنا.
   */
  const quotes = useQuery({
    queryKey: ["lead-deals", record.id, accountId],
    queryFn: () =>
      api<{ items: Array<Record<string, unknown>> }>(
        `/sales/quotations${qs(accountId ? { accountId, pageSize: 50 } : { leadId: record.id, pageSize: 50 })}`,
      ),
  });
  const items = quotes.data?.items ?? [];
  const accepted = !accountId && items.some((q) => q.status === "ACCEPTED");

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">
          <Icon name="briefcase" size={15} /> الصفقات
          {items.length > 0 && (
            <span className="acct-tab-n num">{items.length}</span>
          )}
        </h2>
        {accountId && (
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => navigate(`/crm/accounts/${accountId}`)}
          >
            <Icon name="building" size={13} />
            افتح ملف العميل
          </button>
        )}
      </div>
      <div className="card-body">
        {accepted && (
          <div className="alert alert-warning mb-3">
            <Icon name="star" size={15} />
            <div>
              قُبل عرضٌ لهذا العميل المحتمل — صار مؤهَّلًا. حوّله إلى عميل
              لتنتقل إليه عروضُه وتُبنى عليها العقودُ والفواتير.{" "}
              <button type="button" className="link" onClick={onConvert}>
                حوّله الآن
              </button>
            </div>
          </div>
        )}
        {quotes.isPending ? (
          <div className="skeleton" style={{ height: 120 }} />
        ) : (
          <QuotationsBoard
            accountId={accountId}
            leadId={accountId ? null : record.id}
            quotations={items}
            onInvoiceCreated={(id) => navigate(`/finance/invoices/${id}`)}
            onQualified={(_lead, quotationId) => onQualify(quotationId)}
          />
        )}
      </div>
    </div>
  );
}
